import styled from 'styled-components'
import Link from 'next/link'
import PropTypes from 'prop-types'
import { SecondaryButton } from 'common/button'
import Wrapper from 'common/wrapper'
import { Typography } from 'common/typography'
import { UnorderList } from 'common/lists'
import { dottedBackground } from 'projects/background/background-patron'

const ProjectsSectionStyled = styled.section`
  ${({ background, backgroundStyles }) => background && `${dottedBackground}
    &::before {
      ${backgroundStyles}
    }
  `};
  .section-container {
    display: flex;
    flex-direction: column-reverse;
    margin-bottom: 80px;
  }
  .section-content {
    max-width: 500px;
  }
  .section-icon {
    background: url(/images/projects/landing/icons/background-icon.png);
    width: 80px;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 16px;
    .img {
      position: relative;
      top: 3px;
      right: 2px;
    }
  }
  p {
    padding: 16px 0 24px;
  }
  @media(min-width: 1024px) {
    .section-container {
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};
      margin-bottom: 100px;
    }
    p {
      padding: 16px 0 32px;
    }
    .section-content {
      margin: ${({ reverse }) => (reverse ? '0 0 0 40px' : '0 40px 0 0')};
    }
  }
  @media(min-width: 1366px) {
    .section-container {
      align-items: ${({ align }) => align};
      margin-bottom: ${({ marginBottom }) => `${marginBottom}px`};
    }
    .section-content {
      margin: 0;
    }
  }
`

function ProjectsSection({
  title,
  description,
  cta: { text, link },
  ilustration,
  icon,
  reverse,
  align,
  marginBottom,
  background,
  backgroundStyles,
}) {
  return (
    <ProjectsSectionStyled
      reverse={reverse}
      marginBottom={marginBottom}
      align={align}
      aria-label={title}
      background={background}
      backgroundStyles={backgroundStyles}
    >
      <Wrapper maxWidth="1206">
        <div className="section-container">
          <div className="section-content">
            <div className="section-icon">
              <picture className="img">
                <source srcSet={`/images/projects/landing/icons/${icon}.webp`} type="image/webp" />
                <source srcSet={`/images/projects/landing/icons/${icon}.jpg`} type="image/jpg" />
                <img src={`/images/projects/landing/icons/${icon}.jpg`} width="40" height="40" loading="lazy" alt={`Icono de ${icon}`} />
              </picture>
            </div>
            <Typography color="var(--baseNeutral)" small="headline5" large="headline3" as="h2">
              {title}
            </Typography>
            {typeof description === 'string'
              ? <Typography color="var(--baseNeutral)" as="p">{description}</Typography>
              : <UnorderList keySufix={icon} items={description} gap={32} />
            }
            {text && link && (
              <Link href={link} passHref>
                <SecondaryButton fullWidth fitContent>
                  {text}
                </SecondaryButton>
              </Link>
            )}
          </div>
          {ilustration}
        </div>
      </Wrapper>
    </ProjectsSectionStyled>
  )
}

ProjectsSection.defaultProps = {
  marginBottom: 0,
  align: 'center',
  rowgap: '',
}

ProjectsSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  cta: PropTypes.shape({
    text: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
  }).isRequired,
  ilustration: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
  reverse: PropTypes.bool.isRequired,
  align: PropTypes.string,
  rowgap: PropTypes.string,
  marginBottom: PropTypes.number,
}

export default ProjectsSection
